<template>
  <div class="configuration-workspace content-grid">
    <!-- ACTIONS (tabs + context buttons) -->
    <div class="content-actions">
      <div class="configuration-workspace__tabs">
        <button
          v-for="view in views"
          :key="view.id"
          :class="{
            'tab': true,
            'tab--active': currentView === view.id,
            'tab--disabled': view.disabled
          }"
          :disabled="view.disabled"
          @click="setCurrentView(view.id)"
        >
          <Icon :name="view.icon" />
          {{ view.label }}
          <Badge v-if="view.badge" :variant="view.badge.variant" size="small">{{ view.badge.text }}</Badge>
        </button>
      </div>
      
      <!-- Edit Controls -->
      <div v-if="configStore.editorState.selectedTenant" class="edit-controls">
        <!-- Global Edit/Save Controls -->
        <div v-if="isEditMode" class="edit-mode-active">
          <SegmentedControl
            v-model="editAction"
            :options="editActionOptions"
            size="small"
            @update:model-value="handleEditAction"
          />
        </div>
        
        <!-- Edit All Button (when not in edit mode) -->
        <div v-else-if="hasEditableContent" class="edit-mode-inactive">
          <Button 
            @click="handleEnterGlobalEditMode" 
            :disabled="loading"
            variant="primary" 
            size="small"
            title="Edit all sections you have permission to modify"
            class="edit-all-button"
          >
            <div class="edit-all-button__icon-wrapper">
              <Icon name="pencil-simple" />
              <Badge 
                text="*" 
                variant="default" 
                size="small"
                class="edit-all-button__badge"
              />
            </div>
          </Button>
        </div>

                <!-- Global View Mode Toggle -->
        <SegmentedControl
          v-model="configStore.globalSectionViewMode"
          :options="viewModeOptions"
          size="small"
          @change="handleGlobalViewModeChange"
        />
      </div>
    </div>

    <!-- CONFIG AREA -->
    <div class="content-config">
      <!-- JSON View -->
      <div v-if="currentView === 'json'" class="panel json-panel">
        <div class="panel__header">
          <h3>
            JSON Configuration
            <template v-if="presetSelected">
              <span class="json-preset-label">• Preset:
                {{ selectedPresetName || 'Loading…' }}
              </span>
            </template>
          </h3>
          <div class="panel__toolbar">
            <Button
              @click="formatJson"
              :disabled="!isEditMode || loading || presetSelected"
              :title="presetSelected ? 'Preset JSON is read-only and already pretty-printed' : 'Format JSON'"
              size="small"
              variant="secondary"
            >
              <Icon name="symbol-misc" />
              Format
            </Button>
            <Button @click="validateConfiguration" :disabled="loading" size="small" variant="secondary">
              <Icon name="check" />
              Validate
            </Button>
          </div>
        </div>
        <div class="panel__body monaco-editor-wrapper">
          <MonacoEditor
            v-model="configurationJson"
            language="json"
            :readonly="!isEditMode || presetSelected"
            :show-toolbar="true"
            :theme="monacoTheme"
            @update:model-value="handleJsonChange"
          />
        </div>
      </div>

      <!-- Form/Properties View -->
      <div v-else-if="currentView === 'form'" class="panel properties-panel">
        <div class="panel__body properties-content">
          <div v-if="loading" class="loading-state">
            <Icon name="spinner" :size="48" />
            <p>Loading configuration...</p>
          </div>
          <div v-else-if="configStore.loadingPresets" class="loading-state">
            <Icon name="loading" :size="48" />
            <p>Loading preset configurations...</p>
          </div>
            <div v-else-if="configStore.presetConfigurations.length === 0" class="fallback-editor">
              <ConfigurationProperties :tenant-id="configStore.editorState.selectedTenant" />
            </div>
          <div v-else class="presets-container">
            <div class="preset-details-full">
              <div class="all-presets-view">
                <div class="all-presets-content" ref="presetsScrollContainer">
                  <PresetSection
                    v-for="(config, index) in configStore.presetConfigurations"
                    :key="config.presetInfo.presetId"
                    :id="`preset-section-${index}`"
                    :preset="config"
                    :precedence="presetHierarchy[index]?.precedence"
                    :is-highlighted="configStore.selectedPresetIndex === index"
                    @updateProperty="handlePresetPropertyUpdate"
                    @click.native="handlePresetClick(index)"
                    @node-interaction="handlePresetClick(index)"
                  />
                  <!-- Tenant-level configuration overrides -->
                  <TenantConfigurationSection
                    v-if="shouldShowTenantSection"
                    id="tenant-overrides-section"
                    :is-highlighted="configStore.isTenantOverridesSelected"
                    :tenant-id="configStore.editorState.selectedTenant"
                    :tenant-display-name="selectedTenantDisplayName"
                    :configuration="tenantConfiguration"
                    :precedence="1000"
                    @updateProperty="handleTenantPropertyUpdate"
                    @click.native="handleTenantClick"
                    @node-interaction="handleTenantClick"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Preview View -->
      <div v-else-if="currentView === 'preview'" class="panel preview-panel">
        <div class="panel__header">
          <h3>Configuration Preview</h3>
          <div class="panel__toolbar">
            <Button @click="() => refreshPreview(true)" :disabled="loading" size="small" variant="secondary">
              <Icon name="refresh" />
              Refresh
            </Button>
            <!-- <Toggle v-model="showResolvedTemplates" label="Show Resolved Templates" /> -->
            <Button @click="loadIntrospection" :disabled="loadingIntrospection || !currentConfiguration" size="small" variant="secondary">
              <Icon name="magnifying-glass" />
              Introspect
            </Button>
          </div>
        </div>
        <div class="panel__body preview-content">
          <div v-if="previewData" class="preview-sections">
            <div v-if="templateIntrospection" class="preview-section preview-section--primary">
              <IntrospectionDetails 
                :response="templateIntrospection"
                :theme="monacoTheme"
              />
            </div>

            <div v-else class="preview-section preview-section--primary">
              <h4>Effective Configuration</h4>
              <div class="preview-json-editor">
                <MonacoEditor
                  :model-value="formatPreviewJson(previewData.effective)"
                  language="json"
                  :readonly="true"
                  :show-toolbar="false"
                  :theme="monacoTheme"
                />
              </div>
            </div>
            <div v-if="introspectionError" class="preview-section">
              <div class="error-text">{{ introspectionError }}</div>
            </div>
          </div>
          <div v-else class="empty-state">
            <Icon name="eye" :size="48" />
            <p>No configuration to preview</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { storeToRefs } from 'pinia'
import { Badge, Button, Icon, SegmentedControl } from '@/components/atoms'
import { IntrospectionDetails } from '@/components/molecules'
import { MonacoEditor } from '@/components/organisms'
import ConfigurationProperties from './ConfigurationProperties.vue'
import PresetSection from './PresetSection.vue'
import TenantConfigurationSection from './TenantConfigurationSection.vue'
import { useConfigurationStore } from '@/stores/configuration'
import { useTheme } from '@/composables/useTheme'
import { schemaValidationService, configurationService } from '@/services/api'
import { withApiNotifications } from '@/utils/api-notifications'
import type { JsonPatchOperation } from '@/types/configuration'

const configStore = useConfigurationStore()

// Theme
const { theme } = useTheme()
const monacoTheme = computed(() => theme.value === 'light' ? 'vs' : 'vs-dark')

// State
const previewData = ref<any>(null)
// Use storeToRefs to preserve reactivity (avoid destructuring loss)
const { templateIntrospection, loadingIntrospection, introspectionError } = storeToRefs(configStore)

// View mode options for segmented control
const viewModeOptions = [
  { value: 'form', icon: 'tree-structure', tooltip: 'Switch all sections to Form View' },
  { value: 'json', icon: 'brackets-curly', tooltip: 'Switch all sections to JSON View' }
]

// Edit action options for save/cancel segmented control
const editActionOptions = [
  { value: 'save', icon: 'floppy-disk', tooltip: 'Save changes', disabled: () => !isDirty.value || loading.value },
  { value: 'cancel', icon: 'trash-simple', tooltip: 'Discard changes', disabled: () => loading.value }
]

// Track which action button was clicked (resets after action)
const editAction = ref('')

// Computed
const currentView = computed(() => configStore.editorState.currentView)
const isEditMode = computed(() => configStore.isEditMode)
const isDirty = computed(() => configStore.isDirty)
const loading = computed(() => configStore.loading)
const currentConfiguration = computed(() => configStore.currentConfiguration)
const presetHierarchy = computed(() => configStore.presetHierarchy)

// Extract tenant-level configuration from resolved config
const tenantConfiguration = computed(() => {
  const config = currentConfiguration.value
  if (!config) return null
  
  // Check if tenantConfiguration field exists in the response (Schema V3)
  // Handle both possible property names (backend uses TenantConfiguration with capital T)
  if ('tenantConfiguration' in config) {
    // Return the actual value (could be null, which is fine - allows creating new config)
    return (config as any).tenantConfiguration
  }
  if ('TenantConfiguration' in config) {
    // Return the actual value (could be null, which is fine - allows creating new config)
    return (config as any).TenantConfiguration
  }
  
  // If we have a resolved configuration but no tenant config property, return empty object
  // This allows creating new tenant configuration
  if ('presetSummary' in config && Array.isArray((config as any).presetSummary)) {
    return {}
  }
  
  return null
})

// Determine if tenant section should be shown (even when tenantConfiguration is null)
const shouldShowTenantSection = computed(() => {
  const config = currentConfiguration.value
  if (!config) return false
  
  // Show tenant section if we have a resolved configuration (with presetSummary)
  // This allows users to create tenant configuration even when none exists
  return 'presetSummary' in config && Array.isArray((config as any).presetSummary)
})

const presetSelected = computed(() => configStore.selectedPresetIndex >= 0)
const selectedPresetName = computed(() => {
  if (!presetSelected.value) return undefined
  return configStore.presetConfigurations[configStore.selectedPresetIndex]?.presetInfo?.presetName
})

// Get display name for the selected tenant
const selectedTenantDisplayName = ref<string>('')

// Watch for tenant changes and fetch display name
watch(() => configStore.editorState.selectedTenant, async (tenantName) => {
  if (!tenantName) {
    selectedTenantDisplayName.value = ''
    return
  }
  
  try {
    const tenants = await configurationService.getTenants()
    const tenant = tenants.find(t => t.name === tenantName)
    selectedTenantDisplayName.value = tenant?.displayName || tenantName
  } catch {
    // Fallback to API name if fetch fails
    selectedTenantDisplayName.value = tenantName
  }
}, { immediate: true })

// Reactive references for scroll functionality
const presetsScrollContainer = ref<HTMLElement | null>(null)
const scrollTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
const isScrollingProgrammatically = ref(false)

const configurationJson = computed({
  get: () => {
    // If a specific preset is selected, show that preset's baseConfiguration JSON
    if (configStore.selectedPresetIndex >= 0) {
      const preset = configStore.presetConfigurations[configStore.selectedPresetIndex]
      if (preset) {
        try {
          return JSON.stringify(preset.baseConfiguration, null, 2)
        } catch {
          return '// Failed to render preset JSON' 
        }
      } else {
        return '// Preset not loaded yet or index out of range'
      }
    }
    // Fallback to existing store-computed full/effective configuration
    return configStore.configurationJson
  },
  set: (value: string) => {
    // Prevent editing when a preset is selected (until preset JSON edit diffing is implemented)
    if (configStore.selectedPresetIndex >= 0) {
      return
    }
    try {
      const parsed = JSON.parse(value)
      configStore.updateConfiguration(parsed)
    } catch (err) {
      console.error('Invalid JSON:', err)
    }
  }
})

interface ViewTab {
  id: 'form' | 'json' | 'preview'
  label: string
  icon: string
  disabled: boolean
  badge?: { variant: 'error' | 'warning' | 'success' | 'info'; text: string }
}

const views = computed<ViewTab[]>(() => [
  {
    id: 'form' as const,
    label: 'Configuration',
    icon: 'tree-structure',
    disabled: !currentConfiguration.value,
    badge: undefined
  },
  // {
  //   id: 'json' as const,
  //   label: 'Advanced',
  //   icon: 'brackets-curly',
  //   disabled: false,
  //   badge: validationErrors.value.length > 0 ? {
  //     variant: 'error' as const,
  //     text: validationErrors.value.length.toString()
  //   } : undefined
  // },
  {
    id: 'preview' as const,
    label: 'Preview',
    icon: 'eye',
    disabled: !currentConfiguration.value,
    badge: undefined
  }
])

// Auto-refresh preview when entering the preview tab
watch(currentView, async (newView, oldView) => {
  if (newView === 'preview' && newView !== oldView && currentConfiguration.value) {
    if (import.meta.env.DEV) console.debug('[workspace] entering preview view -> refreshing preview')
    await refreshPreview()
  }
})

// Methods
const setCurrentView = (view: 'json' | 'form' | 'preview') => {
  configStore.setCurrentView(view)
}

const handleGlobalViewModeChange = (mode: string) => {
  // Always set the mode and force sync all sections
  configStore.globalSectionViewMode = mode as 'form' | 'json'
  
  // Always increment the force sync counter to ensure all sections sync,
  // even if they were manually toggled to a different mode
  configStore.globalSectionViewModeForceSync++
}

const handlePresetPropertyUpdate = (
  path: string, 
  value: any, 
  _section: 'base' | 'overrides' | 'inherited',
  presetContext?: {
    presetId: number
    presetName: string
    presetType: string
  }
) => {
  if (!configStore.isEditMode) {
    console.warn('❌ ConfigurationWorkspace: Cannot update property outside of edit mode')
    return
  }
  
  try {
    // Convert dot notation path (e.g. integrations.azureAD.enabled) to JSON Pointer (/integrations/azureAD/enabled)
    // Assumption: Dots denote object nesting and keys themselves do not contain dots.
    // If keys with literal dots are possible, this logic would need a different delimiter strategy.
    const jsonPointerPath = '/' + path.split('.').join('/')
    
    // Create a JSON Patch operation using proper JSON Pointer syntax
    const operation: JsonPatchOperation = {
      op: 'replace',
      path: jsonPointerPath,
      value
    }
  // Debug log removed
    
    // Add the operation to the store with preset context
    configStore.addPatchOperation(operation, presetContext)
  } catch (error) {
    console.error('❌ ConfigurationWorkspace failed to create property update operation:', error)
  }
}

const handleTenantPropertyUpdate = (path: string, value: any) => {
  if (!configStore.isEditMode) {
    console.warn('❌ ConfigurationWorkspace: Cannot update tenant property outside of edit mode')
    return
  }
  
  try {
    // For tenant-level configuration, the path needs to include /tenant/configuration prefix
    const jsonPointerPath = '/tenant/configuration/' + path.split('.').join('/')
    
    // Create a JSON Patch operation for tenant-level configuration
    const operation: JsonPatchOperation = {
      op: 'replace',
      path: jsonPointerPath,
      value
    }
    
    // Add the operation without preset context (tenant-level)
    configStore.addPatchOperation(operation)
  } catch (error) {
    console.error('❌ ConfigurationWorkspace failed to create tenant property update operation:', error)
  }
}

const handleJsonChange = () => {
  // The setter in configurationJson computed will handle this
}

// Check if there's any content the user can edit
const hasEditableContent = computed(() => {
  // Check if user can edit presets or tenant
  return configStore.canEditPresets || configStore.canEditTenants
})

// Edit mode handlers
const handleExitEditMode = async (save: boolean) => {
  try {
    await configStore.exitEditMode(save)
  } catch (err: any) {
    console.error('Failed to exit edit mode:', err)
  }
}

// Enter edit mode for all sections the user has permission to edit
// Only activates sections that have editable fields for the current user
const handleEnterGlobalEditMode = async () => {
  try {
    // First enter global edit mode to establish lock
    await configStore.enterEditMode()
    
    // Then activate edit mode for each section that has editable fields
    // Check if tenant section has any editable fields before activating
    if (configStore.canEditTenants && configStore.sectionHasEditableFields('tenant')) {
      configStore.setSectionEditMode('tenant', undefined, true)
    }
    
    // Activate edit mode only for presets that have editable fields
    for (const preset of configStore.presetConfigurations) {
      if (configStore.sectionHasEditableFields('preset', preset.presetInfo.presetId)) {
        configStore.setSectionEditMode('preset', preset.presetInfo.presetId, true)
      }
    }
  } catch (err: any) {
    console.error('Failed to enter global edit mode:', err)
  }
}

const handleSaveChanges = async () => {
  try {
    await configStore.saveChanges()
  } catch (err: any) {
    console.error('Failed to save changes:', err)
  }
}

// Handle edit action from segmented control
const handleEditAction = async (action: string) => {
  if (action === 'save') {
    await handleSaveChanges()
  } else if (action === 'cancel') {
    await handleExitEditMode(false)
  }
  // Reset selection after action
  editAction.value = ''
}

const formatJson = () => {
  // No-op when preset JSON is selected (already pretty-printed via computed getter)
  if (configStore.selectedPresetIndex >= 0) return
  if (!currentConfiguration.value) return
  const config = 'configuration' in currentConfiguration.value 
    ? currentConfiguration.value.configuration 
    : currentConfiguration.value.baseConfiguration
  const formatted = JSON.stringify(config, null, 2)
  configStore.updateConfiguration(JSON.parse(formatted))
}

const validateConfiguration = async () => {
  if (!currentConfiguration.value) {
    console.warn('No configuration to validate')
    return
  }
  
  try {
    const config = 'configuration' in currentConfiguration.value 
      ? currentConfiguration.value.configuration 
      : currentConfiguration.value.effectiveConfiguration
    
    // Convert configuration to JSON string for validation
    const configJson = JSON.stringify(config, null, 2)
    
    // For now, we'll use a basic JSON schema since we don't have a specific schema endpoint
    // In a real implementation, you'd fetch the appropriate schema for the tenant/preset
    const basicSchema = JSON.stringify({
      "$schema": "http://json-schema.org/draft-07/schema#",
      "type": "object",
      "description": "Basic configuration schema"
    })
    
    await withApiNotifications(
      async () => {
        const result = await schemaValidationService.validateConfiguration({
          json: configJson,
          schema: basicSchema,
          validateMarkdownDescription: true,
          validateEditPresentation: true,
          validateVisibility: true,
          validateCrossReferences: true
        })
        
  // Debug validation result log removed
        return result
      },
      {
        loadingMessage: 'Validating configuration...',
        successMessage: 'Configuration validation completed',
        errorMessage: 'Configuration validation failed',
        notificationOptions: {
          source: { id: 'configuration-workspace', label: 'Configuration Workspace' }
        }
      }
    )
  } catch (error) {
    console.error('Validation error:', error)
  }
}

const refreshPreview = async (force: boolean = false, preserveIntrospection: boolean = false) => {
  if (force) {
    await (configStore as any).forceRefreshConfiguration?.()
  }
  if (!currentConfiguration.value) return

  if (!preserveIntrospection) {
    templateIntrospection.value = null
    introspectionError.value = null
  }
  
  try {
    // Handle different configuration types
    // IMPORTANT: For PresetConfiguration we previously (incorrectly) used baseConfiguration.
    // That excluded inheritedConfiguration + tenantOverrides, so objects added at the
    // tenant level (overrides) like TemplateTesting would not appear. Use effectiveConfiguration instead.
    const effective = 'configuration' in currentConfiguration.value 
      ? currentConfiguration.value.configuration 
      : (
        // currentConfiguration.value is a PresetConfiguration
        (currentConfiguration.value as any).effectiveConfiguration ?? (currentConfiguration.value as any).baseConfiguration
      )
    previewData.value = { effective }
  } catch (err) {
    console.error('Preview refresh error:', err)
  }
}

const loadIntrospection = async () => {
  await refreshPreview(false, true)
  await configStore.loadTemplateIntrospection(10)
}

const formatPreviewJson = (data: any): string => {
  return JSON.stringify(data, null, 2)
}

// Utility: split a semicolon-delimited string into trimmed non-empty lines
// Removed inline error list; semicolon splitting retained elsewhere if needed (function removed here)

// getPresetIcon provided by shared utility

// Watchers
watch(currentConfiguration, () => {
  templateIntrospection.value = null
  introspectionError.value = null
  if (currentView.value === 'preview') {
    refreshPreview()
  }
}, { immediate: true })

watch(currentView, (newView) => {
  if (newView === 'preview') {
    refreshPreview()
  }
})

// Removed auto scroll-to on preset selection to avoid shifting layout unexpectedly.
// If reintroducing, prefer a user-initiated jump with reduced motion respect.

// Add scroll listener to synchronize sidebar selection with scroll position
const handleScrollImmediate = () => {
  if (!presetsScrollContainer.value || configStore.presetConfigurations.length === 0) {
    return
  }
  
  // Scroll debug log removed
  
  const container = presetsScrollContainer.value
  const containerRect = container.getBoundingClientRect()
  const containerTop = containerRect.top
  const containerBottom = containerRect.bottom
  const containerHeight = containerRect.height
  
  // Find which preset is most visible in the viewport
  let mostVisibleIndex = -1
  let maxVisibleHeight = 0
  let debugInfo: any[] = []
  
  for (let index = 0; index < configStore.presetConfigurations.length; index++) {
    const element = document.getElementById(`preset-${index}`)
    if (element) {
      const rect = element.getBoundingClientRect()
      
      // Calculate the visible portion of this preset
      const visibleTop = Math.max(rect.top, containerTop)
      const visibleBottom = Math.min(rect.bottom, containerBottom)
      const visibleHeight = Math.max(0, visibleBottom - visibleTop)
      
      // Also consider if the preset takes up a significant portion of the container
      const visibilityRatio = visibleHeight / Math.min(rect.height, containerHeight)
      
      debugInfo.push({
        index,
        visibleHeight,
        visibilityRatio,
        presetName: configStore.presetConfigurations[index]?.presetInfo?.presetName
      })
      
      // Prefer presets that are more than 30% visible or have the most visible area
      if (visibilityRatio > 0.3 || (visibleHeight > maxVisibleHeight && visibleHeight > 50)) {
        if (visibilityRatio > 0.3 || visibleHeight > maxVisibleHeight) {
          maxVisibleHeight = visibleHeight
          mostVisibleIndex = index
        }
      }
    }
  }
  
  // Scroll debug info log removed
  
  // Update selection if we found a good candidate
  if (mostVisibleIndex >= 0 && mostVisibleIndex !== configStore.selectedPresetIndex) {
  // Updating selection debug log removed
    
    // Set flag to prevent scroll-to behavior
    isScrollingProgrammatically.value = true
    configStore.setSelectedPresetIndex(mostVisibleIndex)
    
    // Reset flag after a short delay to allow for programmatic selections
    setTimeout(() => {
      isScrollingProgrammatically.value = false
    }, 200)
  }
}

// Throttled scroll handler to improve performance
const handleScroll = () => {
  if (scrollTimeout.value) {
    clearTimeout(scrollTimeout.value)
  }
  
  scrollTimeout.value = setTimeout(() => {
    handleScrollImmediate()
  }, 100) // Throttle to 100ms
}

// Add scroll listener on mount
onMounted(() => {
  // Use nextTick to ensure the ref is properly set
  nextTick(() => {
    if (presetsScrollContainer.value) {
      // console.log('Adding scroll listener to container') // Debug log
      presetsScrollContainer.value.addEventListener('scroll', handleScroll, { passive: true })
    } else {
      // console.warn('Presets scroll container not found') // Debug log
    }
  })
})

// Remove scroll listener on unmount
onUnmounted(() => {
  if (presetsScrollContainer.value) {
    presetsScrollContainer.value.removeEventListener('scroll', handleScroll)
  }
  if (scrollTimeout.value) {
    clearTimeout(scrollTimeout.value)
  }
})

// Watch for preset configurations to be loaded and attach scroll listener
watch(() => configStore.presetConfigurations, (newConfigs) => {
  if (newConfigs.length > 0) {
    nextTick(() => {
      if (presetsScrollContainer.value && !presetsScrollContainer.value.hasAttribute('data-scroll-listener')) {
  // Attaching scroll listener debug log removed
        presetsScrollContainer.value.addEventListener('scroll', handleScroll, { passive: true })
        presetsScrollContainer.value.setAttribute('data-scroll-listener', 'true')
      }
    })
  }
}, { immediate: false })

// Guard against stale preset index when preset array changes (e.g., reload)
watch(
  () => [configStore.selectedPresetIndex, configStore.presetConfigurations.length] as const,
  ([index, length]) => {
    if (index >= length) {
      configStore.setSelectedPresetIndex(-1)
    }
  }
)

function handlePresetClick(index: number) {
  configStore.setSelectedPresetIndex(index)
}

function handleTenantClick() {
  configStore.setTenantOverridesSelected(true)
}

</script>

<style scoped lang="scss">
/* Content subgrid: actions (tabs) + config area */
.content-grid { display: grid; grid-template-rows: auto 1fr; grid-template-areas: "actions" "config"; height:100%; }
.content-actions {
  grid-area: actions;
  background: var(--vscode-editorGroupHeader-tabsBackground);
  border-bottom: 1px solid var(--vscode-widget-border);
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  min-height: 3rem;
  padding: 0 calc(2rem + 1px) 0 1rem;
}
.content-config { grid-area: config; min-height:0; overflow:hidden; display:flex; flex-direction:column; }

.configuration-workspace__tabs { 
  display: flex; 
  flex-wrap: wrap;
  align-items: flex-end;
  // padding: 8px 12px 0;
  gap: 0;
  flex: 1;
}

.edit-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}



.edit-mode-active {
  display: flex;
  align-items: center;
  gap: 8px;
}

.edit-mode-inactive {
  display: flex;
  align-items: center;
  gap: 8px;
}

.edit-all-button__icon-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.edit-all-button__badge {
  position: absolute;
  top: -4px;
  right: -6px;
}

.tab {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  background: transparent;
  color: var(--vscode-tab-inactiveForeground);
  border: 1px solid transparent;
  // border-bottom: 1px solid var(--vscode-tab-border);
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: 13px;
  // font-weight: 500;
  position: relative;
  margin-right: 4px;
  border-radius: 4px 4px 0 0;

  &:hover:not(&--disabled):not(&--active) {
    background: var(--vscode-tab-hoverBackground, rgba(255, 255, 255, 0.05));
    color: var(--vscode-tab-hoverForeground);
    border-color: var(--vscode-tab-border);
    // border-bottom-color: var(--vscode-tab-border);
  }

  &--active {
    background: var(--vscode-editor-background);
    color: var(--vscode-tab-activeForeground);
    border-color: var(--vscode-tab-border);
    // border-bottom: 1px;
    // border-bottom-color: var(--vscode-editor-background);
    // font-weight: 600;
    
    // &::after {
    //   content: '';
    //   position: absolute;
    //   bottom: -1px;
    //   left: 0;
    //   right: 0;
    //   height: 2px;
    //   background: var(--vscode-tab-activeBorder, var(--vscode-focusBorder));
    // }
  }

  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;
    border-color: transparent;
  }
}

/* Panels unified */
.panel { display:flex; flex-direction:column; height:100%; min-height:0; overflow:hidden; }
.panel__header { display:flex; justify-content:space-between; align-items:center; padding:12px 16px; background: var(--vscode-editorGroupHeader-tabsBackground); border-bottom:1px solid var(--vscode-widget-border); flex-shrink:0; }
.panel__header h3 { margin:0; font-size:14px; font-weight:600; }
.panel__toolbar { display:flex; align-items:center; gap:8px; }
.panel__body { flex:1; min-height:0; overflow:hidden; display:flex; flex-direction:column; }

.monaco-editor-wrapper {
  flex: 1;
  overflow: hidden;
  height: 100%;
  
  .monaco-editor {
    height: 100% !important;
  }
}

.property-editor {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: var(--vscode-foreground);
  opacity: 0.7;
  
  p {
    margin-top: var(--spacing-md);
    font-size: 14px;
  }
}

.preset-sections {
  height: 100%;
}

.preset-configuration-grid {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xl);
}

// Preserve gap when scrolling to preset or tenant sections
[id^="preset-section-"],
#tenant-overrides-section {
  scroll-margin-top: var(--spacing-xl);
}

.fallback-editor {
  /* Use this when preset configurations fail to load */
  min-height: 200px;
  height: 100%;
}

.presets-container {
  flex: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0; // Critical for flex scrolling
  overflow: hidden; // Allow child to scroll
}

.property-group {
  margin-bottom: 24px;

  h4 {
    margin: 0 0 8px 0;
    font-size: 14px;
    font-weight: 600;
    color: var(--vscode-editor-foreground);
  }

  &__description {
    color: var(--vscode-descriptionForeground);
    font-size: 13px;
    margin: 0;
    font-style: italic;
  }
}

.preview-content {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.preview-sections {
  display: flex;
  flex-direction: column;
  gap: 24px;
  flex: 1;
  min-height: 0;
}

.preview-section {
  h4 {
    margin: 0 0 12px 0;
    font-size: 14px;
    font-weight: 600;
    color: var(--vscode-editor-foreground);
  }
}

.preview-section--primary {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.preview-json-editor {
  flex: 1;
  min-height: 55vh;
}

.preview-section--primary :deep(.introspection-details) {
  flex: 1;
}

.preview-json {
  background: var(--vscode-textBlockQuote-background);
  border: 1px solid var(--vscode-widget-border);
  border-radius: 4px;
  padding: 12px;
  font-family: var(--vscode-editor-font-family);
  font-size: 12px;
  overflow-x: auto;
  white-space: pre-wrap;
  color: var(--vscode-editor-foreground);
}

.error-text { color: var(--vscode-errorForeground); font-size:11px; }

.preset-sources {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.preset-source {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: var(--vscode-list-hoverBackground);
  border-radius: 4px;
  font-size: 12px;

  &__name {
    font-weight: 500;
  }

  &__precedence {
    margin-left: auto;
    color: var(--vscode-descriptionForeground);
    font-family: var(--vscode-editor-font-family);
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: var(--vscode-descriptionForeground);

  p {
    margin: 16px 0 0 0;
    font-size: 14px;
  }
}

.status-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.status-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.edit-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--vscode-statusBar-foreground);
}

.validation-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--vscode-statusBarItem-errorForeground);
}

.etag-display {
  color: var(--vscode-statusBar-foreground);
  font-family: var(--vscode-editor-font-family);
  opacity: 0.8;
}

.properties-content {
  flex: 1;
  height: 100%;
  overflow: hidden; // Essential for child scrolling to work
  display: flex;
  flex-direction: column;
  min-height: 0; // Important for flex containers
}

.preset-sidebar {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--vscode-sideBar-background);
  border-right: 1px solid var(--vscode-widget-border);

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid var(--vscode-widget-border);
    background: var(--vscode-sideBarSectionHeader-background);

    h4 {
      margin: 0;
      font-size: 13px;
      font-weight: 600;
      color: var(--vscode-sideBarTitle-foreground);
    }
  }
}

.preset-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.preset-item {
  padding: 8px 12px;
  margin-bottom: 4px;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.2s ease;

  &:hover {
    background: var(--vscode-list-hoverBackground);
    border-color: var(--vscode-list-hoverBorder);
  }

  &--active {
    background: var(--vscode-list-activeSelectionBackground);
    color: var(--vscode-list-activeSelectionForeground);
    border-color: var(--vscode-focusBorder);
  }

  &__header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;

    .preset-name {
      flex: 1;
      font-weight: 500;
      font-size: 13px;
    }
  }

  &__details {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 11px;
    color: var(--vscode-descriptionForeground);

    .preset-priority {
      font-family: var(--vscode-editor-font-family);
    }

    .preset-overrides {
      display: flex;
      align-items: center;
      gap: 4px;
      color: var(--vscode-editorWarning-foreground);
    }
  }
}

.preset-details {
  height: 100%;
  display: flex;
  flex-direction: column;

  &__content {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
  }

  &__empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: var(--vscode-descriptionForeground);
    text-align: center;

    p {
      margin: 16px 0 0 0;
      font-size: 14px;
    }
  }
}

.preset-details-full {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden; // Ensure child scrolling works
  
  .preset-details__content {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 16px;
    min-height: 0; // Important for flex scrolling
    
    // Force scrollbar visibility for debugging
    &::-webkit-scrollbar {
      width: 8px;
    }
    
    &::-webkit-scrollbar-track {
      background: var(--vscode-scrollbarSlider-background);
    }
    
    &::-webkit-scrollbar-thumb {
      background: var(--vscode-scrollbarSlider-hoverBackground);
      border-radius: 4px;
    }
  }

  .preset-details__empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: var(--vscode-descriptionForeground);
    text-align: center;

    p {
      margin: 16px 0 0 0;
      font-size: 14px;
    }
  }
}

.all-presets-view {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden; // Ensure scrolling container works
  
  .all-presets-header {
    flex-shrink: 0; // Don't shrink the header
    padding: 16px 16px 0;
    margin-bottom: 24px;
    text-align: center;
    
    h3 {
      margin: 0 0 8px 0;
      color: var(--vscode-foreground);
      font-size: 18px;
    }
    
    p {
      margin: 0;
      color: var(--vscode-descriptionForeground);
      font-size: 14px;
    }
  }
  
  .all-presets-content {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 1rem;
    min-height: 0; // Important for flex scrolling
    display: grid;
    gap: 1rem;
    
    // Make scrollbar visible
    &::-webkit-scrollbar {
      width: 8px;
    }
    
    &::-webkit-scrollbar-track {
      background: var(--vscode-scrollbarSlider-background);
    }
    
    &::-webkit-scrollbar-thumb {
      background: var(--vscode-scrollbarSlider-hoverBackground);
      border-radius: 4px;
    }
    
    &::-webkit-scrollbar-thumb:hover {
      background: var(--vscode-scrollbarSlider-activeBackground);
    }
  }
}

/* highlight style defined centrally in PresetSection.vue; duplicate removed */
</style>
